import type { JSX } from 'react';
import { Fragment, useState } from 'react';
import { ActionIcon, Alert, Anchor, Button, Center, Group, Loader, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, overviewApi, timeApi, type TimeCategory, type TimeCategoryInput, type TimeEntryInput, undoPaths } from '../../api';
import { TOTAL_LABEL, UNCATEGORIZED, UNCATEGORIZED_VALUE, formatDate, formatHours, formatPercent, parseGermanNumber, todayIso } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { EditableText } from '../../ui/EditableText';
import { PageHeader, Section } from '../../ui/Section';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';
import { linkTo } from '../../ui/links';
import { categoryChoices, categoryOf } from './timeChoices';

const REMEMBER = 'pst.time.overview.last';
const remembered = (): { projectId?: string; categoryId?: string } => {
  try { return JSON.parse(window.localStorage.getItem(REMEMBER) ?? '{}') as { projectId?: string; categoryId?: string }; } catch { return {}; }
};
const remember = (value: { projectId: string; categoryId: string }): void => {
  try { window.localStorage.setItem(REMEMBER, JSON.stringify(value)); } catch { /* private window */ }
};

/**
 * Hour budgets and bookings across all projects one can see: book time on any
 * project from one place, set budgets, correct one's own recent entries. It
 * writes through the same project routes as the project's own "Zeiten" tab,
 * so both show the same figures.
 */
export function TimeOverviewPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const undo = useUndo();
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canBudget = me?.permissions.includes('projectdata.write') ?? false;
  const data = useQuery({ queryKey: ['time-overview'], queryFn: overviewApi.time });
  const refresh = async (projectId: string): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['time-overview'] });
    await queryClient.invalidateQueries({ queryKey: ['time', projectId] });
  };
  const add = useMutation({ mutationFn: ({ projectId, ...body }: { projectId: string } & TimeEntryInput) => timeApi.addEntry(projectId, body), onSettled: (_r, _e, v) => refresh(v.projectId) });
  const updateEntry = useMutation({
    mutationFn: ({ projectId, id, before, ...body }: { projectId: string; id: string; before: Record<string, unknown> } & TimeEntryInput) => {
      const step = guardedStep('Zeiteintrag', 'PATCH', undoPaths.timeEntry(projectId, id), inversePatch(body, before), body);
      return undo.recordAfter(timeApi.updateEntry(projectId, id, body), step);
    },
    onSettled: (_r, _e, v) => refresh(v.projectId),
  });
  const removeEntry = useMutation({ mutationFn: ({ projectId, id }: { projectId: string; id: string }) => timeApi.removeEntry(projectId, id), onSettled: (_r, _e, v) => refresh(v.projectId) });
  const updateCategory = useMutation({
    mutationFn: ({ projectId, category, ...body }: { projectId: string; category: TimeCategory } & TimeCategoryInput) => {
      const step = guardedStep(`Kontingent ${category.code}`, 'PATCH', undoPaths.timeCategory(projectId, category.id), inversePatch(body, category as unknown as Record<string, unknown>), body);
      return undo.recordAfter(timeApi.updateCategory(projectId, category.id, body), step);
    },
    onSettled: (_r, _e, v) => refresh(v.projectId),
  });

  const last = remembered();
  const [projectId, setProjectId] = useState<string | null>(last.projectId ?? null);
  const [categoryId, setCategoryId] = useState<string | null>(last.categoryId ?? null);
  const [date, setDate] = useState(todayIso());
  const [hours, setHours] = useState('');
  const [taskCode, setTaskCode] = useState('');
  const [comment, setComment] = useState('');
  const error = [add, updateEntry, removeEntry, updateCategory].map((m) => m.error).find(Boolean);

  if (data.isPending) return <Center h={200}><Loader /></Center>;
  if (data.isError) return <Alert color="red">Die Zeiten konnten nicht geladen werden.</Alert>;
  const projects = data.data.projects;
  const project = projects.find((p) => p.projectId === projectId) ?? projects[0];
  const category = categoryId === UNCATEGORIZED_VALUE || project?.categories.some((c) => c.id === categoryId) ? categoryId! : (project?.categories[0]?.id ?? UNCATEGORIZED_VALUE);
  const parsed = parseGermanNumber(hours);
  const valid = project && typeof parsed === 'number' && parsed > 0 && parsed <= 24 && /^\d{4}-\d{2}-\d{2}$/.test(date);
  const byProject = new Map(projects.map((p) => [p.projectId, p]));
  const total = projects.reduce((s, p) => s + p.total, 0);
  const budget = projects.reduce((s, p) => s + p.budget, 0);
  const mine = projects.reduce((s, p) => s + p.mine, 0);
  const listed = projects.filter((p) => p.categories.length > 0 || p.uncategorized.booked > 0);
  // Rest and use in the sum line as in the project lines: only projects with a budget count.
  const withBudget = listed.filter((p) => p.budget > 0);
  const budgetUsed = withBudget.reduce((s, p) => s + p.total, 0);
  const recentHours = data.data.recent.reduce((s, e) => s + e.hours, 0);
  const round2 = (v: number): number => Math.round(v * 100) / 100;

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        <PageHeader title="Zeiten – alle Projekte" description="Stundenkontingente und Buchungen aller Projekte, die Sie sehen dürfen. Änderungen gelten zugleich im jeweiligen Projekt." />
        {error && <Alert color="red" withCloseButton onClose={() => [add, updateEntry, removeEntry, updateCategory].forEach((m) => m.reset())}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}

        <Section title="Zeit erfassen" description="Projekt und Kategorie wählen, Stunden eintragen, Enter.">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!valid || !project) return;
              remember({ projectId: project.projectId, categoryId: category });
              add.mutate(
                { projectId: project.projectId, categoryId: categoryOf(category), date, hours: parsed, taskCode: taskCode.trim() || null, comment: comment.trim() || null },
                { onSuccess: () => { setHours(''); setComment(''); setTaskCode(''); } },
              );
            }}
          >
            <Group gap="xs" align="flex-end" wrap="wrap">
              <Select
                size="sm" label="Projekt" w={300} allowDeselect={false} searchable
                value={project?.projectId ?? null}
                onChange={(v) => { setProjectId(v); setCategoryId(null); }}
                data={projects.map((p) => ({ value: p.projectId, label: `${p.number} – ${p.name}` }))}
                nothingFoundMessage="Kein Projekt gefunden"
              />
              <Select
                size="sm" label="Kategorie" w={300} allowDeselect={false}
                value={category}
                onChange={setCategoryId}
                data={categoryChoices(project?.categories ?? [])}
              />
              <TextInput size="sm" type="date" label="Datum" value={date} onChange={(e) => setDate(e.currentTarget.value)} w={150} />
              <TextInput size="sm" label="Stunden" placeholder="z. B. 2,5" value={hours} onChange={(e) => setHours(e.currentTarget.value)} w={90} />
              <TextInput size="sm" label="Vorgang" placeholder="z. B. 4.11" value={taskCode} onChange={(e) => setTaskCode(e.currentTarget.value)} w={100} />
              <TextInput size="sm" label="Kommentar" value={comment} onChange={(e) => setComment(e.currentTarget.value)} style={{ flex: 1, minWidth: 200 }} />
              <Button type="submit" disabled={!valid} loading={add.isPending}>Erfassen</Button>
            </Group>
          </form>
          {projects.length === 0 && <Text size="sm" c="dimmed">Noch kein Projekt, auf das gebucht werden kann.</Text>}
        </Section>

        <Section
          title="Kontingente"
          description={`${formatHours(total)} erfasst von ${formatHours(budget)} Budget über alle Projekte, davon ${formatHours(mine)} von Ihnen. Budget anklicken zum Ändern.`}
        >
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing={3} className="pst-tabular" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={130}>Kategorie</Table.Th>
                  <Table.Th>Bezeichnung</Table.Th>
                  <Table.Th w={100} ta="right">Budget (h)</Table.Th>
                  <Table.Th w={90} ta="right">erfasst</Table.Th>
                  <Table.Th w={90} ta="right">davon ich</Table.Th>
                  <Table.Th w={90} ta="right">Rest</Table.Th>
                  <Table.Th w={200}>Verbrauch</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {listed.map((p) => (
                  <Fragment key={p.projectId}>
                    <Table.Tr style={{ background: 'var(--mantine-color-default-hover)' }}>
                      <Table.Td colSpan={2}>
                        <Anchor size="sm" fw={700} {...linkTo(navigate, `/projekte/${p.projectId}/zeiten`)}>{p.number}</Anchor>
                        <Text span size="sm" fw={600}> {p.name}</Text>
                      </Table.Td>
                      <Table.Td ta="right"><Text size="sm" fw={700}>{p.budget ? formatHours(p.budget) : '—'}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" fw={700}>{formatHours(p.total)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" c="dimmed">{formatHours(p.mine)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" c={p.budget && p.total > p.budget ? 'red.7' : 'dimmed'}>{p.budget ? formatHours(Math.round((p.budget - p.total) * 100) / 100) : ''}</Text></Table.Td>
                      <Table.Td><Bar share={p.budget ? p.total / p.budget : null} /></Table.Td>
                    </Table.Tr>
                    {p.categories.map((c) => (
                      <Table.Tr key={c.id}>
                        <Table.Td><Text size="sm" ff="monospace" pl="md">{c.code}{c.unpaid ? ' ·' : ''}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed" lineClamp={1} title={c.label ?? undefined}>{c.label}{c.unpaid ? ' (unbezahlt)' : ''}</Text></Table.Td>
                        <Table.Td>
                          <AmountCell value={c.budgetHours} quantity placeholder="—" readOnly={!canBudget} onCommit={(v) => updateCategory.mutate({ projectId: p.projectId, category: c, budgetHours: v === null ? null : Math.max(0, v) })} />
                        </Table.Td>
                        <Table.Td ta="right"><Text size="sm">{formatHours(c.booked)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm" c="dimmed">{c.mine ? formatHours(c.mine) : ''}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm" c={c.remaining !== null && c.remaining < 0 ? 'red.7' : 'dimmed'}>{c.remaining === null ? '' : formatHours(c.remaining)}</Text></Table.Td>
                        <Table.Td><Bar share={c.share} /></Table.Td>
                      </Table.Tr>
                    ))}
                    {p.uncategorized.booked > 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={2}><Text size="sm" fs="italic" c="dimmed" pl="md">{UNCATEGORIZED}</Text></Table.Td>
                        <Table.Td />
                        <Table.Td ta="right"><Text size="sm">{formatHours(p.uncategorized.booked)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm" c="dimmed">{p.uncategorized.mine ? formatHours(p.uncategorized.mine) : ''}</Text></Table.Td>
                        <Table.Td colSpan={2} />
                      </Table.Tr>
                    )}
                  </Fragment>
                ))}
                {listed.length > 0 && (
                  <Table.Tr className="pst-total-row">
                    <Table.Td colSpan={2}><Text size="sm" fw={700}>{TOTAL_LABEL}<Text span size="xs" c="dimmed" fw={400}> · {listed.length === 1 ? '1 Projekt' : `${listed.length} Projekte`}</Text></Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700}>{budget ? formatHours(round2(budget)) : '—'}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700}>{formatHours(round2(total))}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700}>{formatHours(round2(mine))}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700} c={budgetUsed > budget ? 'red.7' : 'dimmed'}>{budget ? formatHours(round2(budget - budgetUsed)) : ''}</Text></Table.Td>
                    <Table.Td><Bar share={budget ? budgetUsed / budget : null} /></Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Section>

        <Section title="Meine letzten Buchungen" description="Stunden und Kommentar anklicken zum Ändern.">
          {data.data.recent.length === 0 ? (
            <Text size="sm" c="dimmed">Noch keine Buchungen.</Text>
          ) : (
            <Table.ScrollContainer minWidth={760}>
              <Table verticalSpacing={2} className="pst-tabular" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={30} />
                    <Table.Th w={110}>Datum</Table.Th>
                    <Table.Th w={220}>Projekt</Table.Th>
                    <Table.Th w={120}>Kategorie</Table.Th>
                    <Table.Th w={90}>Vorgang</Table.Th>
                    <Table.Th w={90} ta="right">Stunden</Table.Th>
                    <Table.Th>Kommentar</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.data.recent.map((e) => {
                    const p = byProject.get(e.projectId);
                    const before = e as unknown as Record<string, unknown>;
                    return (
                      <Table.Tr key={e.id}>
                        <Table.Td><ActionIcon size="xs" variant="subtle" color="gray" aria-label="Eintrag löschen" title="Eintrag löschen" onClick={() => removeEntry.mutate({ projectId: e.projectId, id: e.id })}>✕</ActionIcon></Table.Td>
                        <Table.Td><Text size="sm">{formatDate(e.date)}</Text></Table.Td>
                        <Table.Td><Text size="sm" lineClamp={1} title={p?.name}>{p ? `${p.number} ${p.name}` : ''}</Text></Table.Td>
                        <Table.Td>
                          {e.categoryId === null
                            ? <Text size="sm" fs="italic" c="dimmed">ohne</Text>
                            : <Text size="sm" ff="monospace">{p?.categories.find((c) => c.id === e.categoryId)?.code}</Text>}
                        </Table.Td>
                        <Table.Td><Text size="sm" ff="monospace">{e.taskCode}</Text></Table.Td>
                        <Table.Td><AmountCell value={e.hours} quantity onCommit={(h) => h !== null && h > 0 && h <= 24 && updateEntry.mutate({ projectId: e.projectId, id: e.id, before, hours: h })} /></Table.Td>
                        <Table.Td><EditableText value={e.comment ?? ''} allowEmpty placeholder=" " onCommit={(comment) => updateEntry.mutate({ projectId: e.projectId, id: e.id, before, comment: comment || null })} /></Table.Td>
                      </Table.Tr>
                    );
                  })}
                  <Table.Tr className="pst-total-row">
                    <Table.Td />
                    <Table.Td colSpan={4}><Text size="sm" fw={700}>{TOTAL_LABEL}<Text span size="xs" c="dimmed" fw={400}> · {data.data.recent.length === 1 ? 'diese Buchung' : `diese ${data.data.recent.length} Buchungen`}</Text></Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700}>{formatHours(round2(recentHours))}</Text></Table.Td>
                    <Table.Td />
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Section>
        <ExportsSection manages={me?.permissions.includes('projects.write') ?? false} />
      </Stack>
    </div>
  );
}

function Bar({ share }: { share: number | null }): JSX.Element | null {
  if (share === null) return null;
  const over = share > 1;
  return (
    <Tooltip label={formatPercent(share * 100)}>
      <Group gap={6} wrap="nowrap">
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--mantine-color-default-border)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(share, 1) * 100}%`, height: '100%', borderRadius: 4, background: over ? 'var(--mantine-color-red-6)' : share > 0.8 ? 'var(--mantine-color-orange-5)' : 'var(--mantine-color-brand-6)' }} />
        </div>
        <Text size="xs" c={over ? 'red.7' : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>{formatPercent(share * 100)}</Text>
      </Group>
    </Tooltip>
  );
}

/** Year and month before today's, "JJJJ-MM". */
function lastMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Vorbereitende Exporte (AP-107): hours by month, person and project for
 * payroll or the tax adviser, and the fee statement of all projects as the
 * base of interim invoices. Files, not bookings: PST writes no invoices (A-51).
 */
function ExportsSection({ manages }: { manages: boolean }): JSX.Element {
  const [from, setFrom] = useState(lastMonth);
  const [to, setTo] = useState(lastMonth);
  const month = /^\d{4}-(0[1-9]|1[0-2])$/;
  const valid = (!from || month.test(from)) && (!to || month.test(to)) && (!from || !to || from <= to);
  return (
    <Section
      title="Für Steuer und Buchhaltung"
      description={manages
        ? 'CSV-Dateien zum Weitergeben: Stunden je Monat, Person, Projekt und Kategorie; Honorarstand aller Projekte als Grundlage für Abschlagsrechnungen. PST schreibt keine Rechnungen.'
        : 'Ihre Stunden je Monat, Projekt und Kategorie als CSV, z. B. für die eigene Abrechnung.'}
    >
      <Group gap="xs" align="flex-end" wrap="wrap">
        <TextInput size="xs" type="month" label="von Monat" value={from} onChange={(e) => setFrom(e.currentTarget.value)} w={150} />
        <TextInput size="xs" type="month" label="bis Monat" value={to} onChange={(e) => setTo(e.currentTarget.value)} w={150} error={valid ? undefined : ' '} />
        <Button size="xs" variant="light" component="a" href={valid ? timeApi.monthsUrl(from || null, to || null) : undefined} disabled={!valid} download>
          Stunden je Monat (CSV)
        </Button>
        {manages && <Button size="xs" variant="light" component="a" href={timeApi.allFeesUrl()} download>Honorarstand aller Projekte (CSV)</Button>}
      </Group>
    </Section>
  );
}
